import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import { listenToHostPayout } from "../../redux/payoutsSlice";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { Gift, Award } from "lucide-react";

// 🔹 Helper: format large numbers
const formatNumber = (num: number = 0): string => {
  if (num >= 1_000_000_000) return (num / 1_000_000_000).toFixed(1) + "B";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

const Awards: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { awardPrize, awardsRunnersUp, loading } = useSelector(
    (state: RootState) => state.payouts
  );
  const user = useSelector((state: RootState) => state.auth.user);

  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (user?.userId) dispatch(listenToHostPayout(user.userId));
  }, [dispatch, user]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress sx={{ color: "#bf9b30" }} />
      </Box>
    );

  const handleApply = () => {
    alert("🎉 Application submitted successfully!");
    // Later connect to Firestore or your backend endpoint
  };

  return (
    <Box
      sx={{
        color: "#fff",
        p: 3,
        fontFamily: "'Poppins', sans-serif",
      }}
    >
      <Typography
        variant="h5"
        align="center"
        sx={{
          color: "#bf9b30",
          textTransform: "uppercase",
          mb: 3,
          fontWeight: 600,
          letterSpacing: 1,
        }}
      >
        Awards & Prize
      </Typography>

      {/* 🏆 Award Prize Card */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 200 }}
        style={styles.card}
      >
        <div style={styles.iconBox}>
          <Award size={24} />
        </div>
        <div>
          <h3 style={styles.value}>
            <span style={styles.euroSymbol}>€</span>
            {formatNumber(awardPrize || 0)}
          </h3>
          <p style={styles.label}>Award Prize</p>
        </div>
      </motion.div>

      {/* 🎁 Apply Card */}
      <motion.div
        whileHover={{ scale: 1.05 }}
        transition={{ type: "spring", stiffness: 200 }}
        style={styles.card}
        onClick={() => setExpanded(!expanded)}
      >
        <div style={styles.iconBox}>
          <Gift size={24} />
        </div>
        <div>
          <h3 style={styles.value}>Apply for Prize</h3>
          <p style={styles.label}>Tap to preview and apply</p>
        </div>
      </motion.div>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                background: "rgba(15,15,15,0.85)",
                border: "1px solid rgba(191,155,48,0.3)",
                borderRadius: 3,
                p: 3,
                mt: 2,
              }}
            >
              <TextField
                label="Full Name"
                fullWidth
                value={`${user?.firstName || ""} ${user?.lastName || ""}`}
                sx={fieldStyle}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Email Address"
                fullWidth
                value={user?.emailAddress || user?.email || ""}
                sx={fieldStyle}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Phone Number"
                fullWidth
                value={user?.phoneNumber || ""}
                sx={fieldStyle}
                InputLabelProps={{ shrink: true }}
              />

              <Button
                fullWidth
                onClick={handleApply}
                disabled={!awardsRunnersUp}
                sx={{
                  mt: 3,
                  py: 1.3,
                  fontWeight: 700,
                  borderRadius: 2,
                  background: awardsRunnersUp ? "#bf9b30" : "#777",
                  color: awardsRunnersUp ? "#000" : "#ccc",
                  "&:hover": {
                    background: awardsRunnersUp ? "#d4af37" : "#777",
                  },
                }}
              >
                {awardsRunnersUp ? "Apply Now" : "Runner-up status required"}
              </Button>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default Awards;

// ─── Styles ─────────────────────────────────────────────
const styles: { [key: string]: React.CSSProperties } = {
  card: {
    background: "linear-gradient(145deg, #0a0a0a, #111)",
    border: "1px solid rgba(191,155,48,0.3)",
    borderRadius: 16,
    padding: "20px",
    display: "flex",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 3px 10px rgba(0,0,0,0.4)",
    cursor: "pointer",
    marginBottom: 18,
  },
  iconBox: {
    background: "rgba(191,155,48,0.15)",
    borderRadius: "50%",
    width: 44,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#bf9b30",
  },
  value: {
    fontSize: 22,
    fontWeight: 700,
    color: "#fff",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  euroSymbol: {
    color: "#bf9b30",
    fontWeight: 800,
    fontSize: 20,
  },
  label: { fontSize: 13, color: "#bbb" },
};

// ─── Shared Field Style ─────────────────────────────────
const fieldStyle = {
  my: 1.5,
  "& .MuiOutlinedInput-root": {
    borderRadius: 2,
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#ccc" },
    "&:hover fieldset": { borderColor: "#bf9b30" },
    "&.Mui-focused fieldset": { borderColor: "#bf9b30" },
  },
  "& .MuiInputLabel-root": { color: "#777", fontWeight: 500 },
};
